/**
 * V205: MD → MM 환산 유틸리티
 *
 * MD(Man-Day)를 MM(Man-Month)으로 환산하는 유틸리티입니다.
 *
 * ## 정책 분리 설계
 * - `MM_STANDARD_DAYS`: 기본 환산 기준일 (현재 20일)
 * - `convertMdToMm()`: 환산 함수 (정책 교체 시 이 함수만 수정)
 * - `formatMm()`: MM 표시 포맷팅 (소수점 2자리)
 *
 * ## 향후 확장
 * - 월별 WD(근무일) 기반 환산으로 변경 시:
 *   1. `MmConversionPolicy` 인터페이스에 `workingDays` 필드 추가
 *   2. `convertMdToMm(md, { workingDays: 22 })` 형태로 호출
 *   3. 또는 별도 `convertMdToMmByWd(md, monthWorkingDays)` 함수 추가
 *
 * @module shared/utils/mmConversion
 */

// ============================================
// 환산 정책 상수
// ============================================

/**
 * MM 환산 기준일 (고정값)
 *
 * 현재 정책: 20일 = 1MM
 *
 * @example
 * // 40MD = 2.00MM (40 / 20)
 * // 15MD = 0.75MM (15 / 20)
 *
 * 향후 WD 기반 환산 시 이 상수 대신 월별 WD를 사용하도록 변경
 */
export const MM_STANDARD_DAYS = 20;

// ============================================
// 환산 정책 타입
// ============================================

/**
 * MM 환산 정책 인터페이스
 *
 * 향후 월별 WD 기반 환산 등 확장을 위한 인터페이스
 */
export interface MmConversionPolicy {
  /**
   * 1MM = N일 기준
   *
   * - 'fixed': 고정값 사용 (MM_STANDARD_DAYS)
   * - 'dynamic': workingDays 필드 사용
   */
  type: 'fixed' | 'dynamic';

  /**
   * 동적 환산 시 사용할 근무일수
   *
   * type='dynamic'일 때만 사용
   */
  workingDays?: number;
}

/**
 * 기본 환산 정책 (20일 고정)
 */
export const DEFAULT_MM_POLICY: MmConversionPolicy = {
  type: 'fixed',
};

// ============================================
// 환산 함수
// ============================================

/**
 * MD를 MM으로 환산
 *
 * @param md - Man-Day 값
 * @param policy - 환산 정책 (기본: 20일 고정)
 * @returns Man-Month 값
 *
 * @example
 * // 기본 사용 (20일 기준)
 * convertMdToMm(40);  // 2.0
 * convertMdToMm(15);  // 0.75
 *
 * @example
 * // 동적 WD 기반 환산 (향후 확장)
 * convertMdToMm(22, { type: 'dynamic', workingDays: 22 });  // 1.0
 */
export function convertMdToMm(md: number, policy: MmConversionPolicy = DEFAULT_MM_POLICY): number {
  if (md === 0) return 0;

  const days = policy.type === 'dynamic' && policy.workingDays
    ? policy.workingDays
    : MM_STANDARD_DAYS;

  return md / days;
}

// ============================================
// 포맷팅 함수
// ============================================

/**
 * MM 값을 표시용 문자열로 포맷팅
 *
 * - 소수점 2자리까지 표시 (기존 MD는 1자리, MM은 더 정밀하게)
 * - 0이면 '0.00' 반환
 *
 * @param mm - Man-Month 값
 * @returns 포맷팅된 문자열
 *
 * @example
 * formatMm(2.0);    // '2.00'
 * formatMm(0.75);   // '0.75'
 * formatMm(1.234);  // '1.23'
 */
export function formatMm(mm: number): string {
  return mm.toFixed(2);
}

/**
 * MD를 MM으로 환산하고 포맷팅
 *
 * convertMdToMm + formatMm 조합 헬퍼
 *
 * @param md - Man-Day 값
 * @param policy - 환산 정책
 * @returns 포맷팅된 MM 문자열
 *
 * @example
 * convertAndFormatMm(40);  // '2.00'
 * convertAndFormatMm(15);  // '0.75'
 */
export function convertAndFormatMm(md: number, policy: MmConversionPolicy = DEFAULT_MM_POLICY): string {
  return formatMm(convertMdToMm(md, policy));
}

// ============================================
// V207: 월별 WD 기반 환산 함수
// ============================================

/**
 * V207: MD를 월별 WD 기반으로 MM 환산
 *
 * 기존 고정 20일 기반 대신 해당 월의 실제 근무일수(WD)로 환산
 *
 * @param md - Man-Day 값
 * @param monthWd - 해당 월의 근무일수 (0이면 '-' 반환)
 * @returns MM 값 (monthWd가 0 또는 undefined면 NaN 반환)
 *
 * @example
 * convertMdToMmByWd(22, 22);  // 1.0 (22 / 22)
 * convertMdToMmByWd(44, 22);  // 2.0 (44 / 22)
 * convertMdToMmByWd(10, 20);  // 0.5 (10 / 20)
 */
export function convertMdToMmByWd(md: number, monthWd: number | undefined): number {
  if (!monthWd || monthWd === 0) return NaN;
  if (md === 0) return 0;
  return md / monthWd;
}

/**
 * V207: MD를 월별 WD 기반으로 MM 환산 후 포맷팅
 *
 * monthWd가 0 또는 undefined면 '-' 반환
 *
 * @param md - Man-Day 값
 * @param monthWd - 해당 월의 근무일수
 * @returns 포맷팅된 MM 문자열 또는 '-'
 *
 * @example
 * formatMmByWd(22, 22);     // '1.00'
 * formatMmByWd(44, 22);     // '2.00'
 * formatMmByWd(10, 0);      // '-'
 * formatMmByWd(10, undefined); // '-'
 */
export function formatMmByWd(md: number, monthWd: number | undefined): string {
  const mm = convertMdToMmByWd(md, monthWd);
  if (Number.isNaN(mm)) return '-';
  return formatMm(mm);
}

/**
 * V207: OT분을 MD로 환산
 *
 * @param overtimeMinutes - 추가근무 시간 (분)
 * @returns Man-Day 값
 *
 * @example
 * convertOtMinutesToMd(480);  // 1.0 (480분 = 8시간 = 1MD)
 * convertOtMinutesToMd(240);  // 0.5 (240분 = 4시간 = 0.5MD)
 */
export function convertOtMinutesToMd(overtimeMinutes: number): number {
  if (!overtimeMinutes || overtimeMinutes === 0) return 0;
  return overtimeMinutes / 60 / 8;
}
